/**
 * Bulk invite modal for the Manage Installers page.
 *
 * Paste a batch of emails, pick a pricing tier + brand access once, hit
 * Send Invites — the backend creates locked accounts, emails each invitee
 * a one-time set-password link, and returns a per-email results summary.
 *
 * UX rules:
 *   - Max 50 emails per batch (backend enforces same cap).
 *   - Tier defaults to null (staff must pick); frontend does not pre-set
 *     "Standard" so the caller isn't surprised by a default price band.
 *   - Brand access defaults to nothing checked — user chose "None" so
 *     they must consciously grant access before sending.
 *   - Results panel replaces the form after a successful submit so
 *     staff can copy invite links / spot skipped rows before closing.
 */
import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  X, Send, Users, CheckCircle2, RotateCcw, AlertCircle, SkipForward, Loader2, Mail,
} from 'lucide-react';

import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_EMAILS = 50;

const BRANDS = [
  { id: 'probound', label: 'ProBound', color: '#2F5E96' },
  { id: 'proepoxy', label: 'ProEpoxy', color: '#7A0B7A' },
  { id: 'prodecor', label: 'ProDecor', color: '#D4A24B' },
  { id: 'protraining', label: 'ProTraining', color: '#1f4030' },
];

function authHeaders() {
  return { 'Content-Type': 'application/json' };
}

/**
 * Split whatever staff pasted (commas / newlines / semicolons / spaces)
 * into a deduped, lowercased list. Also flags obviously-malformed items
 * so the counter under the textarea can warn *before* the network call.
 */
function parseEmails(raw) {
  const tokens = String(raw || '')
    .split(/[\s,;]+/)
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);
  const seen = new Set();
  const clean = [];
  const invalid = [];
  const emailRe = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
  for (const t of tokens) {
    if (seen.has(t)) continue;
    seen.add(t);
    if (emailRe.test(t)) clean.push(t);
    else invalid.push(t);
  }
  return { clean, invalid };
}

export default function BulkInviteModal({ open, onClose, pricingTiers = [], onCompleted }) {
  const [emailsRaw, setEmailsRaw] = useState('');
  const [pricingTierId, setPricingTierId] = useState('');
  const [access, setAccess] = useState({ probound: false, proepoxy: false, prodecor: false, protraining: false });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const { clean, invalid } = useMemo(() => parseEmails(emailsRaw), [emailsRaw]);
  const overCap = clean.length > MAX_EMAILS;
  const disabled = busy || clean.length === 0 || overCap;

  const reset = () => {
    setEmailsRaw('');
    setPricingTierId('');
    setAccess({ probound: false, proepoxy: false, prodecor: false });
    setResult(null);
  };

  const handleClose = () => {
    if (busy) return;
    reset();
    onClose();
  };

  const submit = async () => {
    if (disabled) return;
    setBusy(true);
    try {
      const body = {
        emails: clean,
        pricing_tier_id: pricingTierId || null,
        access,
      };
      const r = await fetch(`${API_URL}/api/staff/installers/bulk-invite`, {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `Failed (HTTP ${r.status})`);
      }
      const data = await r.json();
      setResult(data);
      const s = data.summary || {};
      const parts = [];
      if (s.created) parts.push(`${s.created} invited`);
      if (s.restored) parts.push(`${s.restored} restored`);
      if (s.skipped_existing) parts.push(`${s.skipped_existing} skipped`);
      if (s.invalid) parts.push(`${s.invalid} invalid`);
      toast.success(parts.join(' · ') || 'Bulk invite complete');
      if (onCompleted) onCompleted(data);
    } catch (e) {
      toast.error('Bulk invite failed', { description: String(e.message || e).slice(0, 200) });
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      data-testid="bulk-invite-modal"
      onClick={handleClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-[#7A0B7A]/10 text-[#7A0B7A] flex items-center justify-center">
              <Users className="w-5 h-5" />
            </div>
            <div>
              <h2 className="font-bold text-gray-900" data-testid="bulk-invite-title">Bulk invite installers</h2>
              <p className="text-xs text-gray-500">Paste up to {MAX_EMAILS} emails · one click sends set-password links</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-1.5 hover:bg-gray-100 rounded-lg"
            data-testid="bulk-invite-close"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto">
          {result ? (
            <ResultsPanel result={result} onDone={handleClose} onAgain={reset} />
          ) : (
            <>
              {/* Emails textarea */}
              <label className="block text-sm font-semibold text-gray-800 mb-1.5">
                Emails
                <span className="ml-2 text-xs font-normal text-gray-500">
                  Comma, space or newline separated
                </span>
              </label>
              <textarea
                value={emailsRaw}
                onChange={(e) => setEmailsRaw(e.target.value)}
                placeholder={"jane@doe.co.uk\nteam@example.com, mark@installer.co"}
                rows={7}
                className="w-full text-sm font-mono border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#7A0B7A]/40 focus:border-[#7A0B7A]/60"
                data-testid="bulk-invite-emails"
                disabled={busy}
              />
              <div className="flex items-center justify-between mt-1.5 text-xs">
                <div className="flex items-center gap-3">
                  <span
                    className={`font-semibold ${overCap ? 'text-red-600' : 'text-gray-700'}`}
                    data-testid="bulk-invite-count"
                  >
                    {clean.length}/{MAX_EMAILS} valid
                  </span>
                  {invalid.length > 0 && (
                    <span className="text-amber-700" data-testid="bulk-invite-invalid-count">
                      · {invalid.length} malformed
                    </span>
                  )}
                </div>
                {overCap && (
                  <span className="text-red-600 font-semibold">Trim the list to send</span>
                )}
              </div>

              {/* Tier picker */}
              {pricingTiers.length > 0 && (
                <div className="mt-4">
                  <label className="block text-sm font-semibold text-gray-800 mb-1.5">
                    Pricing tier
                    <span className="ml-2 text-xs font-normal text-gray-500">Optional</span>
                  </label>
                  <select
                    value={pricingTierId}
                    onChange={(e) => setPricingTierId(e.target.value)}
                    className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#7A0B7A]/40"
                    data-testid="bulk-invite-tier"
                    disabled={busy}
                  >
                    <option value="">— Leave unassigned —</option>
                    {pricingTiers.map((t) => (
                      <option key={t.tier_id} value={t.tier_id}>
                        {t.label}{t.is_default ? ' (default)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Brand access toggles */}
              <div className="mt-4">
                <label className="block text-sm font-semibold text-gray-800 mb-1.5">
                  Brand access
                  <span className="ml-2 text-xs font-normal text-gray-500">Pre-grant on invite</span>
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {BRANDS.map((b) => {
                    const on = !!access[b.id];
                    return (
                      <button
                        key={b.id}
                        type="button"
                        onClick={() => setAccess((a) => ({ ...a, [b.id]: !a[b.id] }))}
                        disabled={busy}
                        className={`flex items-center gap-2 justify-between border rounded-lg px-3 py-2 text-sm font-semibold transition ${
                          on
                            ? 'text-white border-transparent'
                            : 'text-gray-700 bg-white border-gray-300 hover:border-gray-400'
                        }`}
                        style={on ? { backgroundColor: b.color } : {}}
                        data-testid={`bulk-invite-access-${b.id}`}
                        aria-pressed={on}
                      >
                        <span className="flex items-center gap-2">
                          <span
                            className="inline-block w-2.5 h-2.5 rounded-full"
                            style={{ backgroundColor: on ? '#fff' : b.color }}
                          />
                          {b.label}
                        </span>
                        <span className={`text-[10px] font-bold uppercase tracking-wider ${on ? 'opacity-90' : 'text-gray-400'}`}>
                          {on ? 'On' : 'Off'}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Info line */}
              <div className="mt-4 flex items-start gap-2 text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                <Mail className="w-4 h-4 text-gray-500 shrink-0 mt-0.5" />
                <span>
                  Each invitee gets a one-time <strong>Set my password</strong> link valid for
                  <strong> 7 days</strong>. Existing active accounts are skipped. Soft-deleted
                  accounts are restored and re-invited.
                </span>
              </div>
            </>
          )}
        </div>

        {!result && (
          <div className="border-t border-gray-200 px-5 py-3 flex items-center justify-end gap-2 bg-gray-50">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={busy}
              data-testid="bulk-invite-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={disabled}
              className="bg-[#7A0B7A] hover:bg-[#640964] text-white"
              data-testid="bulk-invite-submit"
            >
              {busy ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                  Sending…
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-1.5" />
                  Send {clean.length || ''} invite{clean.length === 1 ? '' : 's'}
                </>
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function ResultsPanel({ result, onDone, onAgain }) {
  const s = result.summary || {};
  const buckets = [
    { key: 'created', label: 'Invited', rows: result.created || [], color: 'emerald', icon: CheckCircle2 },
    { key: 'restored', label: 'Restored', rows: result.restored || [], color: 'sky', icon: RotateCcw },
    { key: 'skipped_existing', label: 'Skipped (already active)', rows: result.skipped_existing || [], color: 'gray', icon: SkipForward },
    { key: 'invalid', label: 'Invalid', rows: result.invalid || [], color: 'red', icon: AlertCircle },
  ];
  const total = (s.created || 0) + (s.restored || 0) + (s.skipped_existing || 0) + (s.invalid || 0);

  return (
    <div data-testid="bulk-invite-results">
      <p className="text-sm text-gray-600 mb-4">
        Processed <strong>{total}</strong> email{total === 1 ? '' : 's'}
        {s.tier_label ? ` · tier: ${s.tier_label}` : ''}.
      </p>
      <div className="space-y-3">
        {buckets.map((b) => (
          <BucketBlock key={b.key} bucket={b} />
        ))}
      </div>
      <div className="mt-5 flex items-center justify-end gap-2">
        <Button variant="outline" onClick={onAgain} data-testid="bulk-invite-again">
          Invite more
        </Button>
        <Button onClick={onDone} className="bg-[#7A0B7A] hover:bg-[#640964] text-white" data-testid="bulk-invite-done">
          Done
        </Button>
      </div>
    </div>
  );
}

function BucketBlock({ bucket }) {
  const { label, rows, color, icon: Icon } = bucket;
  const count = rows.length;
  if (count === 0) return null;
  const bg = {
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    sky: 'bg-sky-50 border-sky-200 text-sky-900',
    gray: 'bg-gray-50 border-gray-200 text-gray-800',
    red: 'bg-red-50 border-red-200 text-red-900',
  }[color];
  const chipBg = {
    emerald: 'bg-emerald-600 text-white',
    sky: 'bg-sky-600 text-white',
    gray: 'bg-gray-500 text-white',
    red: 'bg-red-600 text-white',
  }[color];
  return (
    <div className={`border rounded-lg ${bg}`} data-testid={`bulk-invite-bucket-${bucket.key}`}>
      <div className="flex items-center gap-2 px-3 py-2 font-semibold text-sm">
        <Icon className="w-4 h-4" />
        <span>{label}</span>
        <span className={`ml-auto text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 ${chipBg}`}>
          {count}
        </span>
      </div>
      <ul className="text-xs px-3 pb-2 space-y-1 font-mono">
        {rows.map((r, idx) => (
          <li key={`${r.email || ""}-${idx}`} className="flex items-center gap-2">
            <span className="truncate">{r.email}</span>
            {r.reason && <span className="text-[10px] uppercase tracking-wider opacity-70">· {r.reason.replace(/_/g, ' ')}</span>}
            {r.invite_sent === false && <span className="text-[10px] uppercase tracking-wider text-amber-700">· email queued</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}
